import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, Outlet, useLocation, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { Divider, Grid, Link as MuiLink, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import { RouterPaths } from '../../router/paths';
import { useGetProfileQuery } from '../../api';
import { permissionsCheck } from '../../utils/permission';
import { useGetMerchantConfigQuery } from '../../api/controllers/merchant';

export const Profile: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { profileUid } = useParams<'profileUid'>();
  const { data: merchantConfig } = useGetMerchantConfigQuery(
    profileUid ? { profileUid } : skipToken
  );
  const hasPermissionToViewBankAccounts = permissionsCheck(['READ_BANK_ACCOUNTS']);
  const hasPermissionToViewDocuments = permissionsCheck(['READ_DOCUMENTS']);
  const hasPermissionToViewTransactions = permissionsCheck(['READ_TRANSACTIONS']);
  const hasPermissionToViewWallets = permissionsCheck(['READ_WALLETS']);
  const hasPermissionToViewMerchants = permissionsCheck(['READ_MERCHANTS']);
  const hasPermissionToViewMerchantCoinConfigs = permissionsCheck(['READ_MERCHANT_COIN_CONFIGS']);

  // api
  const { data: profile } = useGetProfileQuery(profileUid ? { profileUid } : skipToken);

  // variables
  const hasSumsubData = Boolean(profile?.profile.sumsub_applicant_id);
  const isLegalProfile = profile?.profile.type === 'LEGAL';

  // active tab logic
  const tabsPaths = [
    RouterPaths.ProfileDetails,
    RouterPaths.ProfileDocuments,
    RouterPaths.ProfileBankAccounts,
    RouterPaths.ProfileWallets,
    RouterPaths.ProfileTransactions,
    RouterPaths.ProfileMerchantConfig,
    // RouterPaths.ProfileMerchantCoinConfig,
    RouterPaths.ProfileMerchantPayments,
    RouterPaths.ProfileMerchantPaymentDetails,
    RouterPaths.ProfileSumsubInfo,
    RouterPaths.ProfileSandboxUsers,
  ];
  const groupedTabsPaths: Partial<Record<RouterPaths, RouterPaths[]>> = {
    [RouterPaths.ProfileMerchantPayments]: [
      RouterPaths.ProfileMerchantPayments,
      RouterPaths.ProfileMerchantPaymentDetails,
    ],
  };

  const activeTab = tabsPaths.find((tabPath) => {
    if (groupedTabsPaths[tabPath]) {
      return groupedTabsPaths[tabPath]?.some((groupPath) => matchPath(groupPath, pathname));
    }
    return matchPath(tabPath, pathname);
  });

  return (
    <>
      <Stack direction="row" alignItems="flex-end" spacing={2} pl={3} pt={1}>
        <MuiLink underline="hover" component={Link} to={RouterPaths.Profiles}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('profile.back-to-profiles')}
            </Typography>
          </Stack>
        </MuiLink>

        <Divider orientation="vertical" />

        <Typography color="black">{profile?.profile.name}</Typography>
      </Stack>

      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Tabs value={activeTab} variant="scrollable" scrollButtons="auto">
            <Tab
              label={t('profile.tabs.details')}
              component={Link}
              to={`${RouterPaths.Profiles}/${profileUid}/details`}
              value={RouterPaths.ProfileDetails}
              sx={{ textTransform: 'none' }}
            />
            {hasPermissionToViewDocuments && (
              <Tab
                label={t('profile.tabs.documents')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/documents`}
                value={RouterPaths.ProfileDocuments}
                sx={{ textTransform: 'none' }}
              />
            )}
            {hasPermissionToViewBankAccounts && (
              <Tab
                label={t('profile.tabs.bank-accounts')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/bank-accounts`}
                value={RouterPaths.ProfileBankAccounts}
                sx={{ textTransform: 'none' }}
              />
            )}
            {hasPermissionToViewWallets && (
              <Tab
                label={t('profile.tabs.wallets')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/wallets`}
                value={RouterPaths.ProfileWallets}
                sx={{ textTransform: 'none' }}
              />
            )}
            {hasPermissionToViewTransactions && (
              <Tab
                label={t('profile.tabs.transactions')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/transactions`}
                value={RouterPaths.ProfileTransactions}
                sx={{ textTransform: 'none' }}
              />
            )}
            {hasPermissionToViewMerchantCoinConfigs && merchantConfig?.config && (
              <Tab
                label={t('profile.tabs.merchant-config')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/merchant-config`}
                value={RouterPaths.ProfileMerchantConfig}
                sx={{ textTransform: 'none' }}
              />
            )}
            {hasPermissionToViewMerchants && merchantConfig?.config && (
              <Tab
                label={t('profile.tabs.merchant-payments')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/merchant-payments`}
                value={RouterPaths.ProfileMerchantPayments}
                sx={{ textTransform: 'none' }}
              />
            )}
            {hasSumsubData ? (
              <Tab
                label={t('profile.tabs.sumsub-info')}
                component={Link}
                to={`${RouterPaths.Profiles}/${profileUid}/sumsub-info`}
                value={RouterPaths.ProfileSumsubInfo}
                sx={{ textTransform: 'none' }}
              />
            ) : null}
            <Tab
              label={t('profile.tabs.sandbox-users')}
              component={Link}
              to={`${RouterPaths.Profiles}/${profileUid}/sandbox-users`}
              value={RouterPaths.ProfileSandboxUsers}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </Paper>
      </Grid>
    </>
  );
};
